import { useCallback, useEffect, useRef, useState } from 'react';
import { Art } from '../types';
import { isTestMode } from '../testMode';
import { loadArts, saveArts, setStorageUser } from '../utils/storage';

/** Stored selection, defaulting past the first-run gate under `?test=`.
 *  Playwright drives subscription and library flows against a fresh profile,
 *  which would otherwise stop at the art picker — the same reason test mode
 *  mocks auth and subscription state rather than making tests sign in. */
function readArts(uid: string | null): Art[] {
  setStorageUser(uid);
  const stored = loadArts();
  if (stored.length === 0 && isTestMode) return [Art.Bjj];
  return stored;
}

/**
 * The user's chosen grappling arts. Empty until they pick in the ArtPicker
 * gate (App.tsx shows the picker whenever the selection is empty). Persisted
 * per-user in localStorage; setStorageUser must run before every read so the
 * key is scoped to the signed-in uid.
 */
export function useArts(uid: string | null): { arts: Art[]; setArts: (a: Art[]) => void } {
  const [arts, setArtsState] = useState<Art[]>(() => readArts(uid));

  // Re-read when the account changes (sign-in/out), mirroring AppShell's
  // uid-keyed filter reload.
  const prevUid = useRef(uid);
  useEffect(() => {
    if (prevUid.current === uid) return;
    prevUid.current = uid;
    setArtsState(readArts(uid));
  }, [uid]);

  const setArts = useCallback((a: Art[]) => {
    setStorageUser(uid);
    saveArts(a);
    setArtsState(loadArts());
  }, [uid]);

  return { arts, setArts };
}
